"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** A flat map of feature-flag key -> enabled boolean. */
export type FeatureFlagMap = Record<string, boolean>;

interface FeatureFlagContextValue {
  flags: FeatureFlagMap;
  /** True until the initial fetch settles (success or failure). */
  loading: boolean;
}

// Default context: no flags, still loading. Reading a flag before the provider
// mounts (or outside it) therefore fails closed — every flag reads as `false`.
const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  loading: true,
});

interface FeatureFlagProviderProps {
  /**
   * Fetches the caller's evaluated flags as a `{ key: bool }` map. Kept as a
   * prop so this package stays client- and framework-agnostic (no API client,
   * no `next/*`). Called once on mount.
   */
  fetchFlags: () => Promise<FeatureFlagMap>;
  children: ReactNode;
}

/**
 * Fetches the flag map once on mount and holds it in context. On error the map
 * stays empty, so every flag reads as `false` (fail closed). Because unknown
 * flags default to `false` and only turn `true` after a successful fetch, a
 * gated element never flashes on for an untargeted user.
 */
export function FeatureFlagProvider({
  fetchFlags,
  children,
}: FeatureFlagProviderProps) {
  const [flags, setFlags] = useState<FeatureFlagMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchFlags()
      .then((result) => {
        if (active) {
          setFlags(result ?? {});
        }
      })
      .catch(() => {
        if (active) {
          setFlags({});
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
    // Flags are fetched once per page load; `fetchFlags` is treated as stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ flags, loading }), [flags, loading]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Returns whether a flag is enabled for the current user. Returns `false` while
 * loading, on fetch error, and for unknown keys (fail closed).
 */
export function useFeatureFlag(key: string): boolean {
  const { flags } = useContext(FeatureFlagContext);
  return flags[key] ?? false;
}

/** Whether the initial flag fetch is still in flight. */
export function useFeatureFlagsLoading(): boolean {
  return useContext(FeatureFlagContext).loading;
}

interface FeatureGateProps {
  /** The flag key that must be enabled for children to render. */
  flag: string;
  /** Rendered when the flag is off (defaults to nothing). */
  fallback?: ReactNode;
  children: ReactNode;
}

/** Renders `children` only when `flag` is enabled, otherwise `fallback`. */
export function FeatureGate({
  flag,
  fallback = null,
  children,
}: FeatureGateProps) {
  const enabled = useFeatureFlag(flag);
  return <>{enabled ? children : fallback}</>;
}
